import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

const languages = [
  { code: "hu", name: "Magyar", country_code: "hu" },
  { code: "en", name: "English", country_code: "gb" },
  /*{ code: "de", name: "Deutsch", country_code: "de" },
  { code: "es", name: "Español", country_code: "es" },
  { code: "fr", name: "Français", country_code: "fr" },
  { code: "it", name: "Italiano", country_code: "it" },
  { code: "cn", name: "中國人", country_code: "cn" },
  { code: "cz", name: "Čech", country_code: "cz" },
  { code: "ru", name: "Русский", country_code: "ru" },*/
];

const GlobeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="currentColor"
    className="bi bi-globe"
    viewBox="0 0 16 16"
  >
    <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A8 8 0 0 0 5.145 4H7.5zM4.09 4a9.3 9.3 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12q.208.58.468 1.068c.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855q.26-.487.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472c.218.284.418.598.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z" />
  </svg>
);

const TranslatedHeader = () => {
  const { t, i18n } = useTranslation();
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [sticky, setSticky] = useState({ isSticky: false, offset: 0 });
  const [ready, setReady] = useState(i18n.isInitialized); // ✅ Hook a komponens tetején
  const headerRef = useRef(null);
  const currentLang = i18n.language;
  const location = useLocation();
  const navigate = useNavigate();

  // i18n inicializálás figyelése
  useEffect(() => {
    if (!i18n.isInitialized) {
      const onInit = () => setReady(true);
      i18n.on("initialized", onInit);
      return () => i18n.off("initialized", onInit);
    }
  }, [i18n]);

  // Nyelv váltás
  const handleLanguageChange = (code) => {
    if (!ready) return;

    // URL frissítése
    const currentPath = location.pathname;
    const newPath = currentPath.replace(`/${currentLang}/`, `/${code}/`);

    // Nyelv váltás
    i18n.changeLanguage(code).then(() => {
      navigate(newPath);
    });
  };
  // handle scroll event
  // handle scroll event
  const handleScroll = () => {
    const header = headerRef.current;
    if (!header) return;

    // Az aktuális scroll pozíció
    const scrollY = window.scrollY || window.pageYOffset;

    // Ha a scroll pozíció kisebb mint 10px (teljesen felül vagyunk)
    if (scrollY < 10) {
      setSticky({
        isSticky: false,
        offset: 0,
      });
      return;
    }

    // Alapértelmezett sticky logika
    const isNowSticky = scrollY > 100;

    if (sticky.isSticky !== isNowSticky) {
      setSticky({
        isSticky: isNowSticky,
        offset: isNowSticky ? header.offsetHeight : 0,
      });
    }
  };

  // add/remove scroll event listener
  useEffect(() => {
    // Azonnal ellenőrizzük a scroll pozíciót a komponens mountolásakor
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // Üres függőségi lista, csak egyszer regisztráljuk

  // Menüpont URL-ek generálása
  const createLocalizedLink = (path) => `/${currentLang}${path}`;
  const localizedUrl = `https://janoseszter.com${createLocalizedLink("/Home")}`;

  // Hamburger menü bezárása menüpontra kattintáskor
  const handleNavLinkClick = () => {
    setNavbarOpen(false);
  };

  return (
    <header className="bg-white shadow-sm py-1 fixed-top">
      <Helmet>
        <link rel="canonical" href={`https://janoseszter.com/hu/Home`} />
        <meta
          name="description"
          content="To use this application, you need to enable JavaScript in your browser settings."
        />
        <meta
          name="DC.title"
          content="Csillag Masszázs Pedikű | Hivatalos oldal"
        />
        <meta name="DC.language" content={currentLang} />
        <meta property="og:title" content="Csillag Masszázs Pedikűr" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://janoseszter.com/hu/Home`} />
        <meta
          property="og:image"
          content="https://janoseszter.com/images/logo225.png"
        />
        <meta
          property="og:description"
          content="Rövid leírás az oldal tartalmáról."
        />
        <meta property="og:site_name" content="Csillag Masszázs Pedikűr" />

        {/* Google Analytics tag (gtag.js)*/}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-YD6LG1ZNTF"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-YD6LG1ZNTF');
          `}
        </script>
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1684163547956689"
          crossorigin="anonymous"
        ></script>
      </Helmet>

      <h1 style={{ display: "none" }}>{t("Page_Title")}</h1>

      <section
        className="border-bottom py-2"
        style={{ position: "sticky", top: 0, zIndex: 1030 }}
      >
        <div className="container d-flex flex-column flex-md-row justify-content-between align-items-center">
          <div className="text-center text-md-start mb-2 mb-md-0">
            <a
              href="mailto:info@janoseszter.com"
              className="me-3 text-decoration-none text-dark"
            >
              <i className="bi bi-envelope-fill me-1"></i> info@janoseszter.com
            </a>
            <a
              href="tel:+36705541606"
              className="text-decoration-none text-dark"
            >
              <i className="bi bi-telephone-fill me-1"></i> +36 70 554 1606
            </a>
          </div>
          <a
            href="https://www.facebook.com/profile.php?id=61551742774521"
            target="_blank"
            rel="noopener noreferrer"
            className="fs-5 text-dark"
          >
            <i className="bi bi-facebook"></i>
          </a>
        </div>
      </section>

      <nav
        ref={headerRef}
        className={`navbar navbar-expand-lg ${
          sticky.isSticky ? "fixed-top shadow-sm bg-white py-1" : "py-3"
        }`}
        style={{
          top: sticky.isSticky ? "40px" : "0",
          transition: "all 0.4s ease-in-out",
        }}
      >
        <div className="container-fluid  px-1">
          <NavLink
            to={createLocalizedLink("/Home")}
            className="navbar-brand d-flex align-items-center fw-bold"
          >
            <img
              src="../images/logo225.png"
              alt="János Eszter logo"
              style={{
                height: sticky.isSticky ? "50px" : "80px",
                width: sticky.isSticky ? "50px" : "80px",
                transition: "all 0.3s ease",
              }}
            />
            <span
              className="contact-csillag ms-2"
              style={{
                fontSize: sticky.isSticky ? "1.25rem" : "1.5rem",
                fontFamily: "Cormorant Medium Italic",
                transition: "font-size 0.3s",
              }}
            >
              {t("Brand_name")}
            </span>
          </NavLink>

          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setNavbarOpen(!navbarOpen)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div
            className={`collapse navbar-collapse ${navbarOpen ? "show" : ""}`}
          >
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              {[
                "Home",
                "About",
                "Services",
                "TimeSlotTable",
                "Blog",
                "Contact",
              ].map((page) => (
                <li className="nav-item" key={page}>
                  <NavLink
                    className="nav-link"
                    to={createLocalizedLink(`/${page}`)}
                    onClick={handleNavLinkClick}
                  >
                    {t(page)}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/*<div className="dropdown ms-lg-3">
              <button
                className="btn btn-link dropdown-toggle contact-csillag"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <GlobeIcon />
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                {languages.map(({ code, name, country_code }) => (
                  <li key={code}>
                    <button
                      className="dropdown-item"
                      onClick={() => handleLanguageChange(code)}
                    >
                      <span
                        className={`flag-icon flag-icon-${country_code} me-2`}
                      ></span>
                      {name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>*/}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default TranslatedHeader;
